/**
 * Custom Heading Node for Lexical
 * Extends HeadingNode with blockId and direction support for AST compatibility
 */

import { HeadingNode, HeadingTagType, SerializedHeadingNode } from '@lexical/rich-text'
import { $applyNodeReplacement, DOMConversionMap, DOMExportOutput, EditorConfig, LexicalNode, Spread } from 'lexical'

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export type SerializedCustomHeadingNode = Spread<
  {
    blockId: string
    level: HeadingLevel
    dir?: 'ltr' | 'rtl'
  },
  SerializedHeadingNode
>

export class CustomHeadingNode extends HeadingNode {
  __blockId: string
  __level: HeadingLevel

  static getType(): string {
    return 'custom-heading'
  }

  static clone(node: CustomHeadingNode): CustomHeadingNode {
    const cloned = new CustomHeadingNode(node.__blockId, node.__level, node.__key)
    cloned.__dir = node.__dir
    cloned.__format = node.__format
    cloned.__indent = node.__indent
    return cloned
  }

  constructor(blockId: string, level: HeadingLevel, key?: string) {
    const tag: HeadingTagType = `h${level}` as HeadingTagType
    super(tag, key)
    this.__blockId = blockId
    this.__level = level
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getLevel(): HeadingLevel {
    return this.__level
  }

  setLevel(level: HeadingLevel): this {
    const writable = this.getWritable()
    writable.__level = level
    return writable
  }

  getDir(): 'ltr' | 'rtl' | null {
    return this.__dir
  }

  setDir(dir: 'ltr' | 'rtl' | null): this {
    const writable = this.getWritable()
    writable.__dir = dir
    // Keep Lexical's standard direction in sync so element.getDirection() (used by
    // the toolbar and the reconciler) reflects the same value. Otherwise __dir and
    // __direction can diverge — the DOM renders one way but Lexical thinks another.
    writable.setDirection(dir)
    return writable
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      dom.setAttribute('dir', this.__dir)
      dom.style.direction = this.__dir
      dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
    }
    return dom
  }

  updateDOM(prevNode: HeadingNode, dom: HTMLElement): boolean {
    const prevCustom = prevNode as CustomHeadingNode
    if (prevCustom.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevCustom.__dir !== this.__dir) {
      if (this.__dir) {
        dom.setAttribute('dir', this.__dir)
        dom.style.direction = this.__dir
        dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
      } else {
        dom.removeAttribute('dir')
        dom.style.direction = ''
        dom.style.textAlign = ''
      }
    }
    // Return true if tag changed (level changed)
    return prevCustom.__level !== this.__level
  }

  static importDOM(): DOMConversionMap | null {
    return {
      h1: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 1),
        priority: 1,
      }),
      h2: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 2),
        priority: 1,
      }),
      h3: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 3),
        priority: 1,
      }),
      h4: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 4),
        priority: 1,
      }),
      h5: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 5),
        priority: 1,
      }),
      h6: () => ({
        conversion: (element: HTMLElement) => convertHeadingElement(element, 6),
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const tag = `h${this.__level}` as keyof HTMLElementTagNameMap
    const element = document.createElement(tag)
    element.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      element.setAttribute('dir', this.__dir)
    }
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomHeadingNode): CustomHeadingNode {
    const node = $createCustomHeadingNode(serializedNode.blockId, serializedNode.level)
    if (serializedNode.dir) {
      node.setDir(serializedNode.dir)
    }
    node.setFormat(serializedNode.format)
    node.setIndent(serializedNode.indent)
    return node
  }

  exportJSON(): SerializedCustomHeadingNode {
    return {
      ...super.exportJSON(),
      type: 'custom-heading',
      blockId: this.__blockId,
      level: this.__level,
      dir: this.__dir || undefined,
    }
  }
}

function convertHeadingElement(element: HTMLElement, level: HeadingLevel) {
  const blockId = element.getAttribute('data-block-id') || ''
  const dir = element.getAttribute('dir') as 'ltr' | 'rtl' | null
  const node = $createCustomHeadingNode(blockId, level)
  if (dir) {
    node.setDir(dir)
  }
  return { node }
}

export function $createCustomHeadingNode(blockId: string, level: HeadingLevel): CustomHeadingNode {
  return $applyNodeReplacement(new CustomHeadingNode(blockId, level))
}

export function $isCustomHeadingNode(node: LexicalNode | null | undefined): node is CustomHeadingNode {
  return node instanceof CustomHeadingNode
}
